import React, { useRef, useState } from 'react'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import {
  BRIGHTCOVE_PLAYER_ACCOUNT_ID,
  BRIGHTCOVE_VIDEO_PLAYER_ID,
} from '../../../spa/utility'
import { camelCase } from 'lodash'

const formatSeconds = (seconds) => {
  const totalSeconds = (Math.floor(seconds) % 60).toString().slice(0, 2)

  return totalSeconds.length === 1 ? '0' + totalSeconds : totalSeconds
}

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const fomattedSeconds = formatSeconds(seconds)

  return `(${minutes}:${fomattedSeconds})`
}

function VideoPlayer({
  playlistId,
  isInline = false,
  autoplay = false,
  dataTags,
  pdfLinks,
}) {
  const wrapper = useRef()

  const [mediaInfo, setMediaInfo] = useState()

  const onSuccess = (e) => {
    const player = e.ref.player_

    player.on('loadstart', () => {
      setMediaInfo(player.mediainfo)
    })

    e.ref.on('play', onFirstplay)
    e.ref.on('ended', onEnded)

    //delaying running this to give the page time to load so all elements
    //can be correctly targeted
    const timer = setTimeout(() => {
      handleControlbarMenus(player.mediainfo.id)
    }, 1000)
    return () => clearTimeout(timer)
  }

  const onFirstplay = () => {
    if (wrapper.current) {
      wrapper.current.classList.add('currently-playing')
      const videoList = document?.querySelectorAll('.video-player')
      if (videoList && videoList?.length > 0) {
        videoList.forEach((video) => {
          const videoElement = video.querySelector('video-js')
          if (
            videoElement &&
            videoElement.player &&
            videoElement.dataset?.playlistId !== playlistId
          ) {
            videoElement.player.pause()

            videoElement.player.src(videoElement.player.currentSrc())

            videoElement.player.autoplay(false)

            video.classList.remove('currently-playing')
          }
        })
      }
    }
  }

  const onEnded = (e) => {
    if (wrapper.current) {
      wrapper.current.classList.remove('currently-playing')
    }

    e.target.player.src(e.target.player.currentSrc())
  }

  const handleControlbarMenus = (videoId) => {
    let playbackMenu = undefined
    let playbackButton = undefined
    let qualityMenu = undefined
    let qualityButton = undefined
    let controlBar = undefined
    let videoPlayer = undefined

    // add analytics to brightcove video element - tracking play/pause
    const videoElement = dataTags
      ? document.querySelectorAll(`video[data-playlist-id='${playlistId}']`)[0]
      : null

    if (videoElement) {
      Object.keys(dataTags).forEach((tag) => {
        const baseTag = camelCase(tag.replaceAll('data-', ''))
        videoElement.dataset[baseTag] = dataTags[tag]
      })
    }

    const videoList = document.getElementsByTagName('video-js')
    for (const singleVideo of videoList) {
      if (singleVideo.player.mediainfo.id === videoId) {
        videoPlayer = singleVideo
        for (const videoChild of singleVideo.children) {
          if (videoChild.className === 'vjs-control-bar') {
            controlBar = videoChild
            for (const controlChild of videoChild.children) {
              if (controlChild.innerText.includes('Playback Rate')) {
                playbackButton = controlChild
                for (const menuChild of controlChild.children) {
                  if (menuChild.className === 'vjs-menu') {
                    playbackMenu = menuChild
                  }
                }
              }

              if (controlChild.innerText.includes('Quality Levels')) {
                qualityButton = controlChild
                for (const menuChild of controlChild.children) {
                  if (menuChild.className === 'vjs-menu') {
                    qualityMenu = menuChild
                  }
                }
              }
            }
          }
        }
      }
    }

    //adding and removing events for popup menus
    controlBar?.addEventListener('mouseenter', () => {
      playbackMenu?.classList.remove('vjs-menu-popup')
      qualityMenu?.classList.remove('vjs-menu-popup')
    })

    playbackButton?.addEventListener('mouseenter', () => {
      playbackMenu?.classList.add('vjs-menu-popup')
      qualityMenu?.classList.remove('vjs-menu-popup')
      qualityMenu?.classList.remove('vjs-lock-showing')
    })

    qualityButton?.addEventListener('mouseenter', () => {
      playbackMenu?.classList.remove('vjs-menu-popup')
      qualityMenu?.classList.add('vjs-menu-popup')
      qualityMenu?.classList.remove('vjs-lock-showing')
    })

    qualityButton?.addEventListener('click', () => {
      qualityMenu?.classList.remove('vjs-lock-showing')
    })

    playbackMenu?.addEventListener('mouseleave', () => {
      playbackMenu?.classList.remove('vjs-menu-popup')
    })

    qualityMenu?.addEventListener('mouseleave', () => {
      qualityMenu?.classList.remove('vjs-menu-popup')
    })

    videoPlayer?.addEventListener('mouseleave', () => {
      qualityMenu?.classList.remove('vjs-menu-popup')
      playbackMenu?.classList.remove('vjs-menu-popup')
    })
  }

  const { name, longDescription, duration } = mediaInfo || {}

  return (
    <div ref={wrapper} className={`video-player ${isInline ? 'inline' : ''}`}>
      <div className="player-container" data-analytics={name}>
        <ReactPlayerLoader
          accountId={BRIGHTCOVE_PLAYER_ACCOUNT_ID}
          playlistId={playlistId}
          playerId={BRIGHTCOVE_VIDEO_PLAYER_ID}
          onSuccess={onSuccess}
          options={{ autoplay }}
        />
      </div>

      <div className="video-description-container">
        <h4 className="title" dangerouslySetInnerHTML={{ __html: name }} />

        <div
          className="desc"
          dangerouslySetInnerHTML={{ __html: longDescription }}
        />
        {pdfLinks.length != 0 &&
          pdfLinks.map((link, idx) => {
            let [linkText, linkUrl] = link.split(':')
            return (
              <a
                className="u-paddingRight1gu"
                key={idx}
                href={linkUrl}
                target="_blank"
                rel="noreferrer"
              >
                {linkText}
              </a>
            )
          })}
        <div className="duration">
          {(duration && formatTime(duration)) || ''}
        </div>
      </div>
    </div>
  )
}

export default VideoPlayer
